import { strToU8, zipSync } from 'fflate';
import { getGalleryImage } from '@/api/client';
import { productCover } from './kit/Thumb';
import type { GalleryItem } from './types';

const safeName = (name: string) => name.replace(/[\\/:*?"<>|\x00-\x1f]/g, '_').slice(0, 80);
const html = (value: string) => value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);

export async function loadGalleryImages(items: GalleryItem[]) {
  const files: File[] = [];
  for (const [index, item] of items.entries()) {
    const src = productCover(item, true);
    if (!src) throw new Error(`「${item.name}」没有图片，请取消选择后重试。`);
    const url = new URL(src, window.location.href);
    if (url.origin !== window.location.origin) throw new Error(`「${item.name}」使用站外图片，无法安全打包。`);
    const blob = await getGalleryImage(url.pathname);
    const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : blob.type === 'image/gif' ? 'gif' : 'jpg';
    files.push(new File([blob], `${String(index + 1).padStart(2, '0')}-${safeName(item.code)}-${safeName(item.name)}.${ext}`, { type: blob.type }));
  }
  return files;
}

export function shareGalleryImages(files: File[], items: GalleryItem[]) {
  if (!navigator.share || !navigator.canShare?.({ files })) throw new Error('此设备不支持直接分享多张图片，请改用“下载图片”。');
  const list = items.map((item) => `${item.name} · ${item.code} · ${item.category || '未分类'}`).join('\n');
  return navigator.share({ files, title: `产品画册 · ${items.length} 个产品`, text: list });
}

export async function downloadGalleryImages(files: File[], items: GalleryItem[]) {
  const packed: Record<string, Uint8Array> = {};
  for (const file of files) packed[file.name] = new Uint8Array(await file.arrayBuffer());
  packed['产品清单.txt'] = strToU8(items.map((item) => `${item.name}\t${item.code}\t${item.category || '未分类'}`).join('\n'));
  const bytes = zipSync(packed, { level: 0 });
  const url = URL.createObjectURL(new Blob([new Uint8Array(bytes).buffer], { type: 'application/zip' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `产品图片-${files.length}个.zip`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function printGallery(items: GalleryItem[]): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;
  const pages = items.map((item) => {
    const src = productCover(item, true);
    return `<section class="page"><div class="image">${src ? `<img src="${html(new URL(src, window.location.href).href)}" alt="">` : '<p>暂无图片</p>'}</div><h1>${html(item.name)}</h1><p>${html(item.code)} · ${html(item.category || '未分类')}</p></section>`;
  }).join('');
  win.document.write(`<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><title>产品画册</title><style>@page{size:A4;margin:0}*{box-sizing:border-box}body{margin:0;color:#222;font-family:system-ui,-apple-system,"PingFang SC",sans-serif}.page{width:210mm;height:297mm;padding:20mm;page-break-after:always;display:flex;flex-direction:column}.page:last-child{page-break-after:auto}.image{flex:1;min-height:0;display:grid;place-items:center}.image img{max-width:100%;max-height:100%;object-fit:contain}h1{font-size:22px;margin:16px 0 5px}p{font-size:14px;color:#666;margin:0}</style></head><body>${pages}</body></html>`);
  win.document.close();
  const imgs = [...win.document.images];
  void Promise.all(imgs.map((img) => img.complete ? Promise.resolve() : new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); }))).then(() => { win.focus(); win.print(); });
  return true;
}
